import { type RefObject, useCallback, useMemo, useState } from 'react'
import { type Observable } from 'rxjs'
import { useObservable } from './rx-hooks'

type FocusHandler = (...args: never[]) => unknown

/**
 * Props for the hook.
 */
export interface UseFocusProps<F extends FocusHandler> {
  onFocus?: F
  onBlur?: F
}

/**
 * Result for the hook. First item is props to spread to target element, 2nd is
 * whether the element has focus.
 */
export type UseFocusResult<F extends FocusHandler> = [
  { onFocus: F; onBlur: F },
  boolean,
]

/**
 * Focus tracking.
 *
 * @param props
 */
export function useFocusTracking<F extends FocusHandler>(
  props?: UseFocusProps<F>,
): UseFocusResult<F> {
  const [focused, setFocused] = useState(false)
  const onFocusProp = props?.onFocus
  const onBlurProp = props?.onBlur
  const onFocus = useCallback(
    (...args: Parameters<F>) => {
      setFocused(true)
      return onFocusProp?.(...args)
    },
    [onFocusProp],
  ) as F
  const onBlur = useCallback(
    (...args: Parameters<F>) => {
      setFocused(false)
      return onBlurProp?.(...args)
    },
    [onBlurProp],
  ) as F
  const focusProps = useMemo(() => ({ onFocus, onBlur }), [onFocus, onBlur])

  return [focusProps, focused]
}

/**
 * Calls the focus function or `.focus()` on the ref when the `focus$`
 * observable emits.
 *
 * @param focus$ Observable that emits when we need to focus. Should be a
 *  stable reference.
 * @param focusFunctionOrRef This should be stable, don't change from a ref
 *  to a function between renders or vice versa.
 */
export function useObservableFocus(
  focus$: Observable<unknown> | undefined,
  focusFunctionOrRef:
    RefObject<{ focus: () => unknown } | null> | (() => unknown),
) {
  useObservable(focus$, () => {
    if (typeof focusFunctionOrRef === 'function') {
      focusFunctionOrRef()
    } else {
      focusFunctionOrRef.current?.focus()
    }
  })
}
